import React, { useMemo, useCallback, useEffect } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { L3_PATHS } from 'Deer/constants/routes';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import {
  CompensationRootState,
  setIsCompensationRecommendationBottomSheetOpen,
} from 'Deer/intent/Compensation/components/slice/compensationDrawersSlice';
import type { ColDef, ICellRendererParams, GridApi, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Paper } from 'uiToolkitV1/Paper';

import PlanningTableData from '../json/PlanningTable.json';
import { BulkActionContextProvider, useBulkActionContext } from './BulkActionContext';
import { BulkActionHeader } from './BulkActionHeader';
import { checkOutsideGuidelineLimit } from './PlanningMetricCards';
import {
  BaseRateCellRenderer,
  COMPA_RATIO_CELL_RENDERER,
  ChurnLikelihoodCellRenderer,
  CompensationCycleCellRenderer,
  EffectiveDateCellRenderer,
  EmployeeCellRenderer,
  LastDateOfChangeCellRenderer,
  NotesCellRenderer,
  ProposedBaseSalaryCellRenderer,
  RatingCellRenderer,
  RecommendationCellRenderer,
  WaitingOnCellRenderer,
} from './cellRenderers/CellRenderers';

type Props = {
  gridRef: React.RefObject<AgGridReact<Record<string, unknown>>>;
  gridStatePersistenceKey: string;
  activeTab?: string;
};

// Helper for pay columns
const PayCellRenderer = ({ value }: { value: Record<string, unknown> | null }) => {
  if (!value || typeof value !== 'object') return <Box>-</Box>;
  return (
    <Flex h="100%" align="center">
      <Box>
        {value.symbol as string}
        {value.value as string | number}
        {value.percentage !== undefined && <span> ({value.percentage as string | number}%)</span>}
        {value.guidanceValue && (
          <Box fontSize="xs" color="gray.500">
            Guidance: {(value.guidanceValue as { minPercentage: number; maxPercentage: number }).minPercentage}-
            {(value.guidanceValue as { minPercentage: number; maxPercentage: number }).maxPercentage}%
          </Box>
        )}
      </Box>
    </Flex>
  );
};

const COLUMN_CELL_RENDERERS: Record<string, (params: ICellRendererParams) => JSX.Element> = {
  EMPLOYEE: EmployeeCellRenderer,
  APPROVER_NAME: EmployeeCellRenderer,
  COMPENSATION_CYCLE: CompensationCycleCellRenderer,
  RATING: RatingCellRenderer,
  CHURN_LIKELIHOOD: ChurnLikelihoodCellRenderer,
  WAITING_ON: WaitingOnCellRenderer,
  COMPA_RATIO: COMPA_RATIO_CELL_RENDERER,
  BASE_RATE: BaseRateCellRenderer,
  RECOMMENDATION: RecommendationCellRenderer,
  PROPOSED_BASE_SALARY: ProposedBaseSalaryCellRenderer,
  PROPOSED_PAY: PayCellRenderer,
  PROPOSED_RECOMMENDATION: PayCellRenderer,
  APPROVED_PAY: ProposedBaseSalaryCellRenderer,
  FINAL_PAY: PayCellRenderer,
  BUDGET_STATUS: ({ value }) => (
    <Box color={value === 'Exceeded' ? 'red.500' : 'green.500'} fontWeight="600">
      {value || '-'}
    </Box>
  ),
  BUDGET_GRAPH: ({ value }) => {
    if (!value) return <Box>-</Box>;
    return (
      <Box>
        <Box fontSize="12px" color="gray.600">
          Current: ${value.currentPayroll?.toLocaleString?.() || 0}
        </Box>
        <Box fontSize="12px" color="gray.600">
          Proposed: ${value.proposedComp?.toLocaleString?.() || 0}
        </Box>
        <Box fontSize="12px" color="gray.600">
          Budget: ${value.budget?.toLocaleString?.() || 0}
        </Box>
      </Box>
    );
  },
  EFFECTIVE_DATE: EffectiveDateCellRenderer,
  LAST_DATE_OF_CHANGE: LastDateOfChangeCellRenderer,
  NOTES: NotesCellRenderer,
  ACTIONS: ({ value }) => (
    <Box>
      <Box fontSize="12px" color="blue.500" cursor="pointer">
        {value?.options?.[0] || 'Actions'}
      </Box>
    </Box>
  ),
  FLAGGED_PROPOSAL: ({ value }) => (
    <Flex h="100%" align="center" color={value ? 'red.500' : 'green.500'} fontWeight="600">
      {value ? 'Yes' : 'No'}
    </Flex>
  ),
};

const COLUMN_ADDITIONAL_PROPS = {
  WAITING_ON: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.WAITING_ON?.status,
    },
  },
  PROPOSED_BASE_SALARY: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) =>
        checkOutsideGuidelineLimit(params?.data?.PROPOSED_BASE_SALARY, params?.data?.BASE_RATE)
          ? 'Outside Guidelines'
          : 'Within Guidelines',

      values: ['Outside Guidelines', 'Within Guidelines'],
    },
    width: 300,
  },
  PROPOSED_PAY: {
    filter: 'agNumberColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.PROPOSED_PAY?.value,
    },
    width: 250,
  },
  PROPOSED_RECOMMENDATION: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) =>
        params?.data?.PROPOSED_RECOMMENDATION?.value < params?.data?.APPROVED_PAY?.value
          ? 'Outside budget'
          : 'Within budget',

      values: ['Outside budget', 'Within budget'],
    },
    width: 150,
  },
  APPROVED_PAY: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) =>
        checkOutsideGuidelineLimit(params?.data?.APPROVED_PAY, params?.data?.BASE_RATE)
          ? 'Outside Guidelines'
          : 'Within Guidelines',

      values: ['Outside Guidelines', 'Within Guidelines'],
    },
    width: 300,
  },
  FINAL_PAY: {
    filter: 'agNumberColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.FINAL_PAY?.value,
    },
    width: 250,
  },
  CHURN_LIKELIHOOD: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.CHURN_LIKELIHOOD,
    },
    width: 120,
  },
  BUDGET_STATUS: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.BUDGET_STATUS,
    },
  },
  COMPENSATION_CYCLE: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.COMPENSATION_CYCLE,
    },
    width: 150,
  },
  RATING: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => params?.data?.RATING,
    },
    width: 200,
  },
  BASE_RATE: {
    width: 120,
  },
  EFFECTIVE_DATE: {
    width: 150,
  },
  FLAGGED_PROPOSAL: {
    filter: 'agSetColumnFilter',
    filterParams: {
      getValue: (params: ICellRendererParams) => (params?.data?.FLAGGED_PROPOSAL ? 'Yes' : 'No'),
    },
  },
};

const PlanningTableContent = ({ gridRef, gridStatePersistenceKey, activeTab }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { l1Module } = useRouteToNavLevels();
  const isFlaggedProposalsFilterApplied = useSelector(
    (state: CompensationRootState) => state.compensationDrawers.isFlaggedProposalsFilterApplied
  );

  const { tabs, columns, rows } = PlanningTableData;

  // check if the flagged proposals filter is applied and apply ag-grid filter accordingly
  const applyFlaggedProposalFilter = useCallback(() => {
    if (!gridRef.current?.api) {
      return;
    }

    if (isFlaggedProposalsFilterApplied) {
      gridRef.current.api.setFilterModel({
        FLAGGED_PROPOSAL: {
          filterType: 'multi',
          filterModels: [
            null,
            {
              values: ['Yes'],
              filterType: 'set',
            },
            null,
          ],
        },
      });
    } else {
      gridRef.current.api.setFilterModel({
        FLAGGED_PROPOSAL: null,
      });
    }
  }, [isFlaggedProposalsFilterApplied, gridRef]);

  useEffect(() => {
    applyFlaggedProposalFilter();
  }, [isFlaggedProposalsFilterApplied, applyFlaggedProposalFilter]);

  const currentTab = useMemo(() => {
    const foundTab = tabs.find((tab) => tab.id === activeTab);
    if (!foundTab && tabs.length > 0) {
      console.warn(`Tab "${activeTab}" not found, falling back to first tab: ${tabs[0].id}`);
      return tabs[0];
    }
    return foundTab;
  }, [tabs, activeTab]);

  const visibleColumns = useMemo(
    () => (currentTab ? columns.filter((col) => currentTab.visibleColumns.includes(col.columnId)) : columns),
    [columns, currentTab]
  );

  const columnDefs = useMemo<ColDef[]>(
    () =>
      visibleColumns
        .map((col) => ({
          colId: col.columnId,
          headerComponent: CustomHeaderCell,
          sortable: true,
          field: col.columnId,
          headerName: col.displayName,
          cellRenderer: COLUMN_CELL_RENDERERS[col.columnId] || (({ value }) => <Box>{value || '-'}</Box>),
          menuTabs: ['filterMenuTab', 'generalMenuTab'],
          autoHeaderHeight: true,
          wrapHeaderText: true,
          ...(COLUMN_ADDITIONAL_PROPS[col.columnId] || {}),
          rowGroup: col.columnId === 'EMPLOYEE',
          hide: col.columnId === 'EMPLOYEE' || col.columnId === 'FLAGGED_PROPOSAL',
          suppressColumnsToolPanel: col.columnId === 'EMPLOYEE' || col.columnId === 'FLAGGED_PROPOSAL',
        }))
        .filter((col) => col.colId !== 'EMPLOYEE'),
    [visibleColumns]
  );

  const safeRowData = useMemo(
    () =>
      rows.map((row) => {
        const safeRow = { ...row };
        visibleColumns.forEach((col) => {
          if (!(col.columnId in safeRow)) {
            safeRow[col.columnId] = null;
          }
        });
        return safeRow;
      }),
    [rows, visibleColumns]
  );

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: 'Employee',
      field: 'EMPLOYEE',
      pinned: 'left',
      minWidth: 250,
      cellRendererParams: {
        innerRenderer: EmployeeCellRenderer,
        suppressCount: true,
      },
      flex: 1,
      headerComponent: CustomHeaderCell,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: (params) => params.data?.hierarchy?.length === 1,
      menuTabs: ['filterMenuTab'],
      filter: 'agSetColumnFilter',
      filterParams: {
        getValue: (params: ICellRendererParams) => params?.data?.EMPLOYEE?.displayName,
      },
    }),
    []
  );

  // Bulk action context
  const { setSelectedRowIds, resetSelection, setTotalRows } = useBulkActionContext();

  // AG Grid selection handlers
  const handleSelectionChanged = useCallback(
    (event) => {
      const selectedRows = event.api.getSelectedRows()?.filter((row) => row.hierarchy?.length === 1);
      setSelectedRowIds(selectedRows.map((row) => row.id || row.EMPLOYEE?.id || row.APPROVER_NAME?.id));
    },
    [setSelectedRowIds]
  );

  const handleClearSelection = useCallback(() => {
    const api = gridRef.current?.api as GridApi | undefined;
    api?.deselectAll();
  }, [gridRef]);

  const handleSelectAll = useCallback(() => {
    const api = gridRef.current?.api as GridApi | undefined;
    api?.selectAll('filtered');
  }, [gridRef]);

  // Handle filter and data changes for total count
  const handleFilterAndDataChange = useCallback(() => {
    let total = 0;
    const api = gridRef.current?.api as GridApi | undefined;

    if (api?.isAnyFilterPresent()) {
      api.forEachNodeAfterFilter((node) => {
        if (node?.data?.hierarchy?.length === 1) {
          total += 1;
        }
      });
    } else {
      total = safeRowData?.filter((row) => row.hierarchy?.length === 1)?.length || 0;
    }
    setTotalRows(total);
  }, [gridRef, safeRowData?.length, setTotalRows]);

  // Handle row data updates
  const onRowDataUpdated = useCallback(() => {
    resetSelection();
    handleClearSelection();
    handleFilterAndDataChange();
  }, [handleFilterAndDataChange, resetSelection, handleClearSelection]);

  // Update total rows when data changes
  useEffect(() => {
    handleFilterAndDataChange();
  }, [handleFilterAndDataChange]);

  // Add the missing handleAddRow function
  const handleAddRow = useCallback(() => {
    console.log('Add new row');
    // TODO: Implement add row logic
  }, []);

  const handleRowClicked = useCallback(
    (params: RowClickedEvent) => {
      const clickedElement = params.event.target as HTMLElement;
      if (clickedElement?.closest('[data-interactive="true"]')) return;
      if (activeTab === 'propose') {
        if (l1Module === 'company') {
          history.push({
            pathname: L3_PATHS.company.compensation.compensationRecommendation.replace(':employeeId', '1'),
            search: `?source=${l1Module}`,
          });
        } else {
          history.push({
            pathname: L3_PATHS.team.allReports.compensation.compensationRecommendation.replace(':employeeId', '1'),
            search: `?source=${l1Module}`,
          });
        }
        return;
      }
      dispatch(setIsCompensationRecommendationBottomSheetOpen(true));
    },
    [dispatch, activeTab, history, l1Module]
  );

  return (
    <>
      <BulkActionHeader
        clearSelection={handleClearSelection}
        selectAll={handleSelectAll}
        onAddRow={handleAddRow}
        activeTab={activeTab}
      />
      <Paper p="0">
        <Box>
          <AgGridTable
            className="compensation-table compensation-planning-table"
            key={`${gridStatePersistenceKey}-${activeTab}`}
            gridStatePersistenceKey={gridStatePersistenceKey}
            ref={gridRef as any}
            columnDefs={columnDefs}
            rowData={safeRowData}
            domLayout="normal"
            pagination
            paginationPageSize={10}
            headerHeight={36}
            sideBar={null}
            showSidebarToggle={false}
            kebabMenuIsRowSpecific
            kebabMenuOptionsGenerator={() => [
              {
                label: 'Submit',
                onClick: () => {
                  console.log('kebab menu clicked');
                },
              },
            ]}
            rowHeight={88}
            height="calc(100vh - 100px)"
            autoGroupColumnDef={autoGroupColumnDef}
            treeData
            animateRows
            groupDefaultExpanded={0}
            getDataPath={(data) => data.hierarchy}
            onSelectionChanged={handleSelectionChanged}
            onRowDataUpdated={onRowDataUpdated}
            onFilterChanged={onRowDataUpdated}
            rowSelection="multiple"
            suppressRowClickSelection
            // add bg color to the row group when expanded
            getRowStyle={(params) => {
              const { node } = params;
              const isParent = node.childrenAfterGroup && node.childrenAfterGroup.length > 0;

              if (isParent && node.expanded) {
                return { backgroundColor: '#FAFAFA' };
              }

              if (node.level > 0 && node.parent?.expanded) {
                return { backgroundColor: '#FAFAFA' };
              }

              return {};
            }}
            // redraw rows when row group is opened because expand state is not updated
            onRowGroupOpened={(params) => params.api.redrawRows({ rowNodes: [params.node] })}
            onGridReady={applyFlaggedProposalFilter}
            onRowClicked={handleRowClicked}
          />
        </Box>
      </Paper>
    </>
  );
};

// Main component that provides the context
const PlanningTable = (props: Props) => (
  <BulkActionContextProvider>
    <Box background="white" padding="12px" borderRadius="8px">
      <PlanningTableContent {...props} />
    </Box>
  </BulkActionContextProvider>
);

export { PlanningTable };
